"use client";

import { useState } from "react";
import { ExternalLink, Navigation } from "lucide-react";
import { textos, type Idioma } from "@/lib/directorio/idiomas";

/**
 * El mapa del negocio, dentro de la ficha.
 *
 * Antes habia un boton que se iba a Google: quien queria situar el sitio tenia
 * que salir de Zindra para volver despues. Ahora se ve aqui, y salir es una
 * opcion, no la unica forma.
 *
 * El mapa es de OpenStreetMap por tres razones: no necesita clave ni
 * facturacion, ya citamos a OSM en cada pagina porque de ahi vienen los datos
 * del directorio, y su incrustado oficial esta hecho justo para esto. Va con
 * `loading="lazy"`: es contenido de otro dominio y no puede retrasar el pintado
 * de una pagina que tiene que cargar rapido.
 *
 * EL MAPA NACE QUIETO. Un mapa incrustado se queda con la rueda del raton: si
 * alguien baja por la ficha y pasa por encima, la pagina se para y el mapa hace
 * zoom. Por eso lleva delante una capa que se lleva el raton hasta que se hace
 * clic; entonces el mapa es de quien lo ha pedido, y vuelve a quedarse quieto
 * en cuanto el raton se va.
 */
export function MapaDelNegocio({
  latitud,
  longitud,
  nombre,
  idioma,
}: {
  latitud: number;
  longitud: number;
  nombre: string;
  idioma: Idioma;
}) {
  const t = textos(idioma);

  const [interactivo, setInteractivo] = useState(false);

  // Un cuadrado de unos 400 metros de lado: se ve la calle y las de al lado,
  // que es lo que hace falta para situarse.
  //
  // EL RECORTE VA CASI CUADRADO desde que el mapa dejo de vivir en una barra
  // estrecha y ocupa el ancho de la pagina: con el recorte apaisado de antes,
  // una caja alta enseñaba medio barrio arriba y abajo del negocio.
  const margen = 0.0025;
  const bbox = [
    longitud - margen,
    latitud - margen / 1.15,
    longitud + margen,
    latitud + margen / 1.15,
  ].join(",");

  const incrustado = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitud},${longitud}`;
  const completo = `https://www.openstreetmap.org/?mlat=${latitud}&mlon=${longitud}#map=17/${latitud}/${longitud}`;
  const comoLlegar = `https://www.google.com/maps/dir/?api=1&destination=${latitud},${longitud}`;

  return (
    <div className="mt-5">
      <div
        className="relative overflow-hidden rounded-lg border border-zinc-200"
        onMouseLeave={() => setInteractivo(false)}
      >
        <iframe
          src={incrustado}
          title={`${nombre} — ${t.verEnElMapa}`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="block h-72 w-full border-0 sm:h-80"
        />
        {!interactivo && (
          <button
            type="button"
            onClick={() => setInteractivo(true)}
            className="absolute inset-0 flex cursor-pointer items-center justify-center opacity-0 transition-opacity hover:opacity-100 focus-visible:opacity-100"
          >
            <span className="rounded-md bg-white/90 px-3 py-1.5 text-sm text-zinc-700 shadow-sm">
              {t.activarMapa}
            </span>
          </button>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm">
        <a
          href={completo}
          rel="noopener noreferrer nofollow"
          target="_blank"
          className="inline-flex items-center gap-1 text-zinc-500 transition-colors hover:text-zinc-900"
        >
          {t.mapaCompleto}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
        <a
          href={comoLlegar}
          rel="noopener noreferrer nofollow"
          target="_blank"
          className="inline-flex items-center gap-1 text-zinc-500 transition-colors hover:text-zinc-900"
        >
          <Navigation className="h-3.5 w-3.5" aria-hidden />
          {t.comoLlegar}
        </a>
      </div>
    </div>
  );
}
